import { CheckSharp } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import StorageIcon from '@mui/icons-material/Storage';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';

import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
  // useMediaQuery,
  // useTheme,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useState } from 'react';
import ai_solution from '../../../assets/svgs/ai_solution.svg';
import { ContextResponse, StepData } from '../../../common/types/solution';
import { useGraphQLQuery } from '../../../customHooks/useGraphQL';
import { GET_ENTERPRISE_CONTEXT_FILTERS } from '../../../graphql/queries/solutionsQueries';
import { generatedOpportunities } from '../../../services/llm_api_services';
import { EmbeddedIcon } from '../legacy/EmbeddedIcon';
import OpportunityModal from './OpportunityModal';
import { useDispatch, useSelector } from 'react-redux';
import { updateDiagramData1 } from '../store/diagramViewerSlice';
import { RootState } from '../../../store';
import { saveDiagram } from '../../../services/diagramService';
import ShowToast, { ToastTypeEnum } from '../../../common/components/ShowToast';
import { BpmnModeler, GeneratedOpportunityApi } from '../Types/diagram-type';

interface ElementData {
  id: string;
  type: string;
  name: string;
  description?: string;
  aiEnablers?: string[];
}

interface Opportunity {
  id: string;
  name?: string;
  description?: string;
  shape_id: string;
  valid?: boolean;
  checked?: boolean;
  data?: {
    knowledgeData: string[];
    'PoC Complexity': string | undefined | number;
    complexity?: number;
    mustHave?: string;
  };
}

const getPriorityInfo = (priority?: string) => {
  switch (priority) {
    case 'GOOD TO HAVE':
      return {
        label: 'OPTIONAL',
        icon: ThumbUpIcon,
        bgColor: '#094db1',
        textColor: '#ffffff',
      };
    case 'MUST HAVE':
      return {
        label: 'ESSENTIAL',
        icon: ThumbUpIcon,
        bgColor: '#FFD700',
        textColor: '#ffffff',
      };
    default:
      return {
        label: 'ESSENTIAL',
        icon: ThumbUpIcon,
        bgColor: '#FFD700',
        textColor: '#ffffff',
      };
  }
};

const getComplexityInfo = (complexity?: number) => {
  switch (complexity) {
    case 1:
      return {
        label: 'Low',
        icon: SignalCellularAltIcon,
        color: '#4caf50',
      };
    case 2:
      return {
        label: 'Medium',
        icon: SignalCellularAltIcon,
        color: '#ff9800',
      };
    case 3:
      return {
        label: 'High',
        icon: SignalCellularAltIcon,
        color: '#f44336',
      };
    default:
      return {
        label: 'Medium',
        icon: SignalCellularAltIcon,
        color: '#ff9800',
      };
  }
};

interface DiagramModalProps {
  isOpen: boolean;
  elementData: ElementData | null;
  activeTab: 'aiHubble' | 'opportunities';
  opportunities?: Opportunity[];
  onClose: () => void;
  onTabChange: (tab: 'aiHubble' | 'opportunities') => void;
  onOpportunityAdd?: (opportunity: Opportunity) => void;
  onStepDescriptionUpdate?: () => void;
  diagramId?: string;
  diagramXml?: string;
  bpmnModelerRef?: React.MutableRefObject<BpmnModeler | undefined>;
}

const DiagramModal: React.FC<DiagramModalProps> = ({
  isOpen,
  elementData,
  activeTab,
  opportunities = [],
  onClose,
  // onTabChange,
  onOpportunityAdd,
  onStepDescriptionUpdate,
  diagramId,
  diagramXml,
  bpmnModelerRef,
}) => {
  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const diagramData = useSelector(
    (state: RootState) => state.diagramViewer.diagramData?.DiagramData?.diagram?.data
  );

  const stepDataFromStore = useSelector(
    (state: RootState) =>
      state.diagramViewer.diagramData?.DiagramData?.diagram?.data?.[elementData?.id || '']
  );
  const isEditPermission = useSelector((state: RootState) => state.diagramViewer.diagramData);
  const isEditing = isEditPermission?.selectedCard?.processType === 'custom';

  const [isFormOpen, setFormOpen] = React.useState(false);
  const [formData, setFormData] = React.useState({
    name: '',
    content: '',
    knowledgeData: [''],
  });
  const [opportunitiesList, setOpportunitiesList] = React.useState<Opportunity[]>([]);
  const [editingOpportunity, setEditingOpportunity] = React.useState<Opportunity | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = React.useState<Opportunity | null>(null);

  const [editableName, setEditableName] = useState(elementData?.name || '');
  const [editableDescription, setEditableDescription] = useState(elementData?.description || '');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (!isOpen || !elementData?.id) {
      setEditableName('');
      setEditableDescription('');
      return;
    }

    if (stepDataFromStore) {
      setEditableName(stepDataFromStore.name || '');
      setEditableDescription(stepDataFromStore.description || '');
      return;
    }
    try {
      const elementRegistry = (bpmnModelerRef as any)?.current?.get?.('elementRegistry');
      const element = elementRegistry?.get?.(elementData.id);
      const nameFromElement = element?.businessObject?.name || '';
      setEditableName(nameFromElement);
    } catch (e) {
      setEditableName('');
    }

    setEditableDescription('');
  }, [isOpen, elementData?.id, stepDataFromStore, bpmnModelerRef]);
  useEffect(() => {
    if (!bpmnModelerRef?.current || !elementData?.id) return;

    const eventBus = (bpmnModelerRef.current as any).get('eventBus');

    const handleLabelChange = (event: any) => {
      const element = event.element;
      if (!element || element.id !== elementData.id) return;

      const newName = element.businessObject?.name || '';

      setEditableName(newName);

      dispatch(
        updateDiagramData1({
          [element.id]: {
            name: newName,
            description: editableDescription,
          },
        })
      );

      void saveDiagramAndData(newName, editableDescription);
    };

    eventBus.on('commandStack.element.updateLabel.postExecute', handleLabelChange);

    return () => {
      eventBus.off('commandStack.element.updateLabel.postExecute', handleLabelChange);
    };
  }, [bpmnModelerRef, elementData?.id, editableDescription]);

  useEffect(() => {
    if (!bpmnModelerRef?.current || !elementData?.id) return;

    const elementRegistry = (bpmnModelerRef.current as any).get('elementRegistry');
    const element = elementRegistry?.get(elementData.id);

    if (element?.businessObject?.name) {
      setEditableName(element.businessObject.name);
      // saveDiagramAndData(element.businessObject.name);
    }
  }, [elementData?.id]);

  useEffect(() => {
    const modeler = bpmnModelerRef?.current;
    if (!modeler || !elementData?.id) return;

    const eventBus = (modeler as any).get('eventBus');

    const handler = (e: any) => {
      const updatedElement = e.element;
      if (updatedElement?.id === elementData.id) {
        const newName = updatedElement.businessObject?.name;
        if (newName && newName !== editableName) {
          setEditableName(newName);

          const currentDescription =
            stepDataFromStore?.description ??
            diagramData?.[elementData.id]?.description ??
            editableDescription;

          saveDiagramAndData(newName, currentDescription);
        }
      }
    };

    eventBus.on('element.changed', 500, handler);
    eventBus.on('elements.changed', 500, handler);

    return () => {
      eventBus.off('element.changed', handler);
      eventBus.off('elements.changed', handler);
    };
  }, [
    bpmnModelerRef,
    elementData?.id,
    editableName,
    editableDescription,
    stepDataFromStore,
    diagramData,
  ]);

  const saveDiagramAndData = async (newName: string, newDescription?: string) => {
    if (!isEditing) return;
    if (!diagramData || !elementData || !diagramId) return;

    setIsUpdating(true);
    try {
      let xmlToSave = diagramXml || '';
      if (bpmnModelerRef?.current) {
        const result = await bpmnModelerRef.current.saveXML({ format: true });
        if (result?.xml) {
          xmlToSave = result.xml;
        }
      }

      const updatedDiagramData = {
        ...diagramData,
        [elementData.id]: {
          name: newName,
          description: newDescription ?? editableDescription,
        },
      };

      dispatch(
        updateDiagramData1({
          [elementData.id]: {
            name: newName,
            description: newDescription ?? editableDescription,
          },
        })
      );

      const formData = new FormData();
      formData.append('xmlData', new File([xmlToSave], 'diagram.xml', { type: 'text/xml' }));
      formData.append('data', JSON.stringify(updatedDiagramData));

      await saveDiagram(diagramId, formData);

      if (onStepDescriptionUpdate) {
        onStepDescriptionUpdate();
      }

      ShowToast({ message: 'Updated successfully', type: ToastTypeEnum.Success });
    } catch (err) {
      console.error('Failed to update step description:', err);
      ShowToast({ message: 'Failed to update step description', type: ToastTypeEnum.Error });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateDiagramData = async () => {
    if (!isEditing) return;
    if (!diagramData || !elementData || !diagramId) return;

    if (bpmnModelerRef?.current && elementData?.id) {
      const modeling = (bpmnModelerRef.current as any).get('modeling');
      const elementRegistry = (bpmnModelerRef.current as any).get('elementRegistry');
      const element = elementRegistry?.get(elementData.id);
      if (element && modeling) {
        modeling.updateLabel(element, editableName || '');
        if (element.businessObject) {
          element.businessObject.name = editableName || '';
        }
      }
    }
    dispatch(
      updateDiagramData1({
        [elementData.id]: {
          name: editableName,
          description: editableDescription,
        },
      })
    );

    // await saveDiagramAndData(editableName || '', editableDescription);
    onClose();
  };

  const [isAiModalOpen, setIsAiModalOpen] = React.useState(false);

  const { refetch: fetchEnterpriseContext } = useGraphQLQuery<{
    enterpriseContext: {
      fetch: Array<{
        id: string;
        createdAt: string;
        updatedAt: string;
        enterpriseContext: string;
      }>;
    };
  }>(GET_ENTERPRISE_CONTEXT_FILTERS, { lazy: true });
  const [isLoading, setIsLoading] = React.useState(false);
  const currentOrg = localStorage.getItem('current_organization') || 'Empty';

  React.useEffect(() => {
    if (opportunities && isOpen && elementData) {
      const filteredOpportunities = opportunities.filter((opp) => opp.shape_id === elementData.id);
      setOpportunitiesList(filteredOpportunities);
    }
  }, [opportunities, isOpen, elementData]);

  if (!isOpen || !elementData) return null;

  // const handleTabChange = (_event: React.SyntheticEvent, newValue: 'aiHubble' | 'opportunities') => {
  //     onTabChange(newValue);
  // };

  const handleEditClick = (opportunity: Opportunity) => {
    setEditingOpportunity(opportunity);
    setFormData({
      name: opportunity.name || '',
      content: opportunity.description || '',
      knowledgeData: opportunity.data?.knowledgeData || [''],
    });
    setFormOpen(true);
  };

  const handleDeleteClick = (opportunity: Opportunity) => {
    setOpportunityToDelete(opportunity);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (opportunityToDelete) {
      setOpportunitiesList((prev) => prev.filter((opp) => opp.id !== opportunityToDelete.id));
      setIsDeleteModalOpen(false);
      setOpportunityToDelete(null);
    }
  };
  const handleGenAiSolutions = async () => {
    try {
      setIsLoading(true);
      setIsAiModalOpen(false);
      console.log('currentOrg:', currentOrg);
      const { data } = await fetchEnterpriseContext({
        v1: { organizationId: currentOrg },
        v2: ['-updated_at'],
      });

      if (data?.enterpriseContext?.fetch) {
        const contextData = data.enterpriseContext.fetch;

        const shape_id = elementData?.id || '';
        const step: StepData = {
          step: elementData?.name || 'Untitled Step',
          step_description: elementData?.description || '',
        };

        const contextResponse: ContextResponse = {
          diagram_id: '',
          shape_id,
          step,
          enterpriseContext: contextData[0]?.enterpriseContext || '',
        };

        const generatedOpportunitiesData = await generatedOpportunities(contextResponse);
        if (Array.isArray(generatedOpportunitiesData)) {
          const newOpportunities: Opportunity[] = generatedOpportunitiesData.map(
            (opp: GeneratedOpportunityApi): Opportunity => ({
              id: opp.id ?? Date.now().toString(),
              name: opp.name ?? 'Untitled Opportunity',
              description: opp.description ?? '',
              shape_id: opp.shape_id ?? shape_id,
              valid: true,
              checked: false,
              data: {
                knowledgeData: Array.isArray(opp.data?.knowledgeData) ? opp.data.knowledgeData : [],
                'PoC Complexity': opp.data?.complexity ?? 'Medium',
                complexity: opp.data?.complexity,
                mustHave: opp.data?.mustHave,
              },
            })
          );

          setOpportunitiesList((prev) => [...prev, ...newOpportunities]);
        } else {
          console.error('No opportunities generated');
        }

        setIsAiModalOpen(false);
      } else {
        console.error('No enterprise context data found');
        setIsAiModalOpen(false);
      }
    } catch (error) {
      console.error('Error generating AI solutions:', error);
      setIsAiModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormClose = () => {
    if (
      formData.name.trim() ||
      formData.content.trim() ||
      formData.knowledgeData.some((d) => d.trim())
    ) {
      const updatedOpportunity: Opportunity = {
        id: editingOpportunity?.id || Date.now().toString(),
        name: formData.name.trim() || 'Untitled Opportunity',
        description: formData.content.trim(),
        shape_id: elementData?.id || '',
        valid: true,
        checked: editingOpportunity?.checked || false,
        data: {
          knowledgeData: formData.knowledgeData.filter((item) => item.trim()),
          'PoC Complexity': 'Medium',
          complexity: 2,
          mustHave: 'GOOD TO HAVE',
        },
      };

      if (editingOpportunity) {
        setOpportunitiesList((prev) =>
          prev.map((opp) => (opp.id === editingOpportunity.id ? updatedOpportunity : opp))
        );
      } else {
        setOpportunitiesList((prev) => [...prev, updatedOpportunity]);
      }

      if (onOpportunityAdd) {
        onOpportunityAdd(updatedOpportunity);
      }
    }

    setFormData({
      name: '',
      content: '',
      knowledgeData: [''],
    });
    setFormOpen(false);
    setEditingOpportunity(null);
  };

  const handleAddKnowledgeData = () => {
    setFormData((prev) => ({
      ...prev,
      knowledgeData: [...prev.knowledgeData, ''],
    }));
  };

  const handleRemoveKnowledgeData = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      knowledgeData: prev.knowledgeData.filter((_, i) => i !== index),
    }));
  };

  const handleKnowledgeDataChange = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      knowledgeData: prev.knowledgeData.map((item, i) => (i === index ? value : item)),
    }));
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        aria-labelledby="step-description-dialog-title"
        BackdropProps={{
          sx: {
            backgroundColor: 'transparent',
          },
        }}
        PaperProps={{
          sx: {
            position: 'fixed',
            right: 40,
            top: 155,
            m: 0,
            height: '70vh',
            width: { xs: '100%', sm: 520 },
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle
          id="step-description-dialog-title"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            pb: 1,
            fontWeight: 600,
          }}
        >
          Step Description
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
          {/* Tabs */}
          {/* Hiding Tabs From Hackett Hubble */}
          {/* <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            borderBottom: 1,
                            mb: 2,
                            flexShrink: 0,
                        }}
                    >
                        <Tab label="AI Hubble" value="aiHubble" />
                        <Tab label="Opportunities" value="opportunities" />
                    </Tabs> */}

          <Box
            sx={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {activeTab === 'aiHubble' && (
              <Stack spacing={3} sx={{ pb: 2, overflowY: 'auto', pr: 1, height: '100%' }}>
                <Box>
                  <Typography variant="h6" gutterBottom fontSize={14} fontWeight={500}>
                    Name
                  </Typography>
                  <TextField
                    fullWidth
                    value={editableName}
                    onChange={(e) => setEditableName(e.target.value)}
                    placeholder="Enter name"
                    disabled={isUpdating || !isEditing}
                  />
                </Box>

                <Box mt={2}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    fontSize={14}
                    fontWeight={500}
                    sx={{ mb: 1 }}
                  >
                    Description
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    maxRows={24}
                    value={editableDescription}
                    onChange={(e) => setEditableDescription(e.target.value)}
                    placeholder="Enter description"
                    disabled={isUpdating || !isEditing}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        padding: 0,
                      },
                      '& textarea': {
                        overflow: 'auto',
                        resize: 'none',
                        padding: 2,
                        margin: 0,
                      },
                    }}
                  />
                </Box>
              </Stack>
            )}

            {activeTab === 'opportunities' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    p: { xs: 1, sm: 2 },
                    flexShrink: 0,
                  }}
                >
                  {isLoading ? (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: '#1b54f8',
                        width: 24,
                        height: 24,
                      }}
                    />
                  ) : (
                    <Box
                      component="img"
                      src={ai_solution}
                      alt="AI Solutions Icon"
                      sx={{
                        width: 24,
                        height: 24,
                        cursor: 'pointer',
                      }}
                      onClick={() => setIsAiModalOpen(true)}
                    />
                  )}
                  <AddIcon
                    onClick={() => setFormOpen(true)}
                    sx={{ color: '#666', fontSize: 24, cursor: 'pointer' }}
                  />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    px: { xs: 1, sm: 2 },
                    pb: 2,
                    '&::-webkit-scrollbar': {
                      width: '6px',
                      height: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '10px',
                      '&:hover': {
                        background: '#666',
                      },
                    },
                  }}
                >
                  {(opportunities.length > 0 || opportunitiesList.length > 0) && (
                    <Stack spacing={{ xs: 1, sm: 2 }}>
                      {(opportunitiesList.some((opp) => opp.valid === true)
                        ? [...opportunitiesList].sort((a, b) => {
                            if (a.valid === b.valid) return 0;
                            return a.valid ? -1 : 1;
                          })
                        : opportunitiesList
                      ).map((opp) => {
                        const complexityInfo = getComplexityInfo(opp.data?.complexity);
                        const ComplexityIcon = complexityInfo.icon;
                        const priorityInfo = getPriorityInfo(opp.data?.mustHave);
                        const PriorityIcon = priorityInfo.icon;

                        return (
                          <Card
                            key={opp.id}
                            variant="outlined"
                            sx={{
                              borderRadius: { xs: 1, sm: 2 },
                              border: '2px solid #DAA520',
                              '&:hover': {
                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                              },
                            }}
                          >
                            <CardContent
                              sx={{
                                p: { xs: 1.5, sm: 2 },
                                '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Checkbox
                                  size="small"
                                  color="primary"
                                  checked={opp.valid || false}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setOpportunitiesList((prev) => {
                                      const updatedList = prev.map((item) =>
                                        item.id === opp.id ? { ...item, valid: checked } : item
                                      );
                                      if (onOpportunityAdd) {
                                        // Pass all opportunities for the current element to update badges correctly
                                        const allOpportunities = updatedList;
                                        onOpportunityAdd(
                                          Object.assign({}, opp, {
                                            valid: checked,
                                            allOpportunities,
                                          })
                                        );
                                      }
                                      return updatedList;
                                    });
                                  }}
                                />
                                <Typography variant="h6">{opp.name}</Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {opp.description}
                              </Typography>
                              {opp.data?.knowledgeData && opp.data.knowledgeData.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                  }}
                                >
                                  {opp.data.knowledgeData.map((knowledge, idx) => (
                                    <Box
                                      key={idx}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1.5,
                                        borderBottom:
                                          idx !== opp.data!.knowledgeData.length - 1
                                            ? '1px solid'
                                            : 'none',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      <StorageIcon sx={{ fontSize: 16, color: 'grey' }} />
                                      <Typography variant="body2">{knowledge}</Typography>
                                    </Box>
                                  ))}
                                </Box>
                              )}

                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mt: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    width: ' 100%',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  <Tooltip title="Embedded">
                                    <IconButton
                                      size="small"
                                      sx={{
                                        color: 'grey.600',
                                        '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.04)' },
                                      }}
                                    >
                                      <EmbeddedIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={`${priorityInfo.label}`}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        bgcolor: priorityInfo.bgColor,
                                        borderRadius: '16px',
                                        border: '1px solid rgba(255,255,255,1)',
                                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                        px: 1,
                                        py: 0.5,
                                        gap: 0.5,
                                      }}
                                    >
                                      <PriorityIcon
                                        sx={{
                                          fontSize: 14,
                                          color: priorityInfo.textColor,
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: priorityInfo.textColor,
                                          fontWeight: 600,
                                          fontSize: '11px',
                                        }}
                                      >
                                        {priorityInfo.label}
                                      </Typography>
                                    </Box>
                                  </Tooltip>

                                  <Tooltip title={complexityInfo.label}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: '8px',
                                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                                      }}
                                    >
                                      <ComplexityIcon
                                        sx={{
                                          fontSize: 16,
                                          color: complexityInfo.color,
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditClick(opp)}
                                      sx={{
                                        filter: 'hue-rotate(350deg) saturate(0) brightness(1)',
                                        '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.04)' },
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteClick(opp)}
                                      sx={{
                                        color: 'grey.600',
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        {isEditing && (
          <DialogActions sx={{ p: 2 }}>
            <Button
              sx={{ width: '100px' }}
              variant="contained"
              color="primary"
              onClick={handleUpdateDiagramData}
              disabled={isUpdating}
            >
              {isUpdating ? <CircularProgress size={20} color="inherit" /> : 'Update'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <OpportunityModal
        isOpen={isFormOpen}
        formData={formData}
        onClose={handleFormClose}
        onFormDataChange={setFormData}
        onAddKnowledgeData={handleAddKnowledgeData}
        onRemoveKnowledgeData={handleRemoveKnowledgeData}
        onKnowledgeDataChange={handleKnowledgeDataChange}
        isDeleteModalOpen={isDeleteModalOpen}
        onDeleteModalClose={() => setIsDeleteModalOpen(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onSave={handleFormClose}
      />
      {/* Gen ai modal */}
      <Modal
        open={isAiModalOpen}
        onClose={() => setIsAiModalOpen(false)}
        aria-labelledby="generate-ai-opportunities-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card
          sx={{
            position: 'relative',
            width: { xs: '90%', sm: 400 },
            maxWidth: '95vw',
            borderRadius: { xs: 1, sm: 2 },
            overflow: 'hidden',
            boxShadow: 24,
          }}
        >
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: '#1a73e8',
              color: 'white',
            }}
          >
            <Typography variant="h6">Generate AI opportunities?</Typography>
            <IconButton
              onClick={() => setIsAiModalOpen(false)}
              size="small"
              sx={{ color: 'white' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{
              bgcolor: 'white',
              p: 3,
              color: 'text.primary',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <Typography>AI Opportunities will be generated, this may take a while.</Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                onClick={() => setIsAiModalOpen(false)}
                startIcon={<CloseIcon fontSize="small" />}
                sx={{
                  bgcolor: '#e0e0e0',
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: '#d5d5d5',
                  },
                }}
              >
                No
              </Button>
              <Button
                variant="contained"
                onClick={handleGenAiSolutions}
                startIcon={<CheckSharp fontSize="small" />}
                sx={{
                  bgcolor: '#1a73e8',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#0641e6',
                  },
                }}
              >
                Yes
              </Button>
            </Box>
          </Box>
        </Card>
      </Modal>
    </>
  );
};

export default DiagramModal;
